import { useState } from "react";
import { toast, Toaster } from "sonner";
import { Reveal } from "../components/Reveal";
import Seo from "../components/Seo";
import { submitInquiry } from "../lib/api";

const PROJECT_TYPES = ["Hotel", "Resort", "Restaurant", "Bar / Club", "Spa / Wellness", "Branded Residence", "Other"];

export default function Contact() {
    const [form, setForm] = useState({
        name: "",
        email: "",
        phone: "",
        company: "",
        project_type: "",
        location: "",
        budget: "",
        message: "",
        website: "", // honeypot — must remain blank
    });
    const [submitting, setSubmitting] = useState(false);
    const [submitted, setSubmitted] = useState(false);

    const change = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

    const onSubmit = async (e) => {
        e.preventDefault();
        if (!form.name || !form.email || !form.message) {
            toast.error("Please fill in your name, email, and message.");
            return;
        }
        // Client-side email sanity
        if (!/^\S+@\S+\.\S+$/.test(form.email)) {
            toast.error("Please enter a valid email address.");
            return;
        }
        setSubmitting(true);
        try {
            const res = await submitInquiry({
                ...form,
                form_type: "contact",
                submitted_page:
                    typeof window !== "undefined" ? window.location.pathname : "/contact",
            });
            setSubmitted(true);
            toast.success(res.message || "Thank you — we'll be in touch shortly.");
            setForm({
                name: "",
                email: "",
                phone: "",
                company: "",
                project_type: "",
                location: "",
                budget: "",
                message: "",
                website: "",
            });
        } catch (err) {
            const msg = err?.response?.data?.detail || "Something went wrong. Please try again.";
            toast.error(typeof msg === "string" ? msg : "Could not submit inquiry.");
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <main data-testid="page-contact" className="pt-32 md:pt-40">
            <Seo
                path="/contact"
                title="Contact — Begin a Project"
                description="Tell us about your property. ASC Interior Co., Ltd. accepts a small, considered slate of new commissions each year."
            />
            <Toaster position="top-center" richColors />

            <section className="px-6 md:px-12 lg:px-24 pb-20">
                <Reveal>
                    <p className="asc-overline">Begin</p>
                </Reveal>
                <Reveal delay={100}>
                    <h1 className="serif text-5xl sm:text-6xl lg:text-7xl mt-6 leading-[1.02] tracking-tight font-light max-w-5xl">
                        Have a vision? <br />
                        Let&apos;s <span className="italic">shape it</span> together.
                    </h1>
                </Reveal>
                <Reveal delay={180}>
                    <p className="mt-10 max-w-2xl text-lg lg:text-xl text-[#5C5855] leading-relaxed">
                        We take on a small, considered slate of projects each year.
                        Share your brief and we&apos;ll be in touch within two working days.
                    </p>
                </Reveal>
            </section>

            <section className="px-6 md:px-12 lg:px-24 pb-32">
                <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-20">
                    <div className="lg:col-span-4 order-2 lg:order-1">
                        <div className="border-t border-[#E5E1DB] pt-8">
                            <p className="asc-overline">Studio</p>
                            <p className="mt-5 text-base text-[#1A1A1A] leading-relaxed">
                                ASC Interior Co., Ltd.
                                <br />
                                313 Thong Lo 15 Alley
                                <br />
                                Khlong Tan Nuea, Watthana
                                <br />
                                Bangkok 10110, Thailand
                            </p>
                        </div>
                        <div className="border-t border-[#E5E1DB] pt-8 mt-12">
                            <p className="asc-overline">Reach</p>

                            <p className="mt-5 text-[10px] uppercase tracking-[0.22em] text-[#8C7A6B]">
                                For project inquiries
                            </p>
                            <a href="mailto:info@ascinterior.com" className="!block mt-1 asc-link w-fit" data-testid="contact-email">
                                info@ascinterior.com
                            </a>
                            <a href="tel:+6621853103" className="!block mt-1 asc-link w-fit" data-testid="contact-phone">
                                +66 2 185 3103
                            </a>

                            <p className="mt-5 text-[10px] uppercase tracking-[0.22em] text-[#8C7A6B]">
                                Career opportunities
                            </p>
                            <a href="mailto:hr@ascinterior.com" className="!block mt-1 asc-link w-fit" data-testid="contact-hr-email">
                                hr@ascinterior.com
                            </a>

                            <p className="mt-5 text-[10px] uppercase tracking-[0.22em] text-[#8C7A6B]">
                                Supplier & brand partnerships
                            </p>
                            <a href="mailto:aschub@ascinterior.com" className="!block mt-1 asc-link w-fit" data-testid="contact-partnerships-email">
                                aschub@ascinterior.com
                            </a>
                        </div>
                        <div className="border-t border-[#E5E1DB] pt-8 mt-12">
                            <p className="asc-overline">Follow</p>
                            <div className="mt-5 flex items-center gap-4 text-[#1A1A1A]">
                                <a href="https://www.facebook.com/ascinterior" target="_blank" rel="noreferrer" className="hover:text-[var(--asc-accent)] transition-colors" aria-label="Facebook">Facebook</a>
                                <span className="text-[#D1CAC2]">·</span>
                                <a href="https://www.instagram.com/ascinterior/" target="_blank" rel="noreferrer" className="hover:text-[var(--asc-accent)] transition-colors" aria-label="Instagram">Instagram</a>
                                <span className="text-[#D1CAC2]">·</span>
                                <a href="https://www.linkedin.com/company/asc-interiors-co.-ltd/" target="_blank" rel="noreferrer" className="hover:text-[var(--asc-accent)] transition-colors" aria-label="LinkedIn">LinkedIn</a>
                            </div>
                        </div>
                    </div>

                    <form
                        data-testid="contact-form"
                        onSubmit={onSubmit}
                        className="lg:col-span-8 order-1 lg:order-2"
                    >
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-10 gap-y-6">
                            <Field label="Name" required>
                                <input data-testid="contact-name" className="asc-input" value={form.name} onChange={change("name")} placeholder="Your full name" />
                            </Field>
                            <Field label="Email" required>
                                <input data-testid="contact-email-input" type="email" className="asc-input" value={form.email} onChange={change("email")} placeholder="you@company.com" />
                            </Field>
                            <Field label="Company / Brand">
                                <input data-testid="contact-company" className="asc-input" value={form.company} onChange={change("company")} placeholder="Optional" />
                            </Field>
                            <Field label="Phone">
                                <input data-testid="contact-phone-input" type="tel" className="asc-input" value={form.phone} onChange={change("phone")} placeholder="Optional" />
                            </Field>
                            <Field label="Project type">
                                <select data-testid="contact-project-type" className="asc-input" value={form.project_type} onChange={change("project_type")}>
                                    <option value="">Select…</option>
                                    {PROJECT_TYPES.map((t) => (
                                        <option key={t} value={t}>{t}</option>
                                    ))}
                                </select>
                            </Field>
                            <Field label="Location">
                                <input data-testid="contact-location" className="asc-input" value={form.location} onChange={change("location")} placeholder="City, Country" />
                            </Field>
                            <Field label="Indicative budget">
                                <input data-testid="contact-budget" className="asc-input" value={form.budget} onChange={change("budget")} placeholder="Optional" />
                            </Field>
                        </div>
                        <div className="mt-10">
                            <Field label="A few notes about the project" required>
                                <textarea data-testid="contact-message" rows={5} className="asc-input" value={form.message} onChange={change("message")} placeholder="Brief, scope, target opening, anything we should know." />
                            </Field>
                        </div>
                        {/* Honeypot field — hidden from real users; bots that fill everything trip it */}
                        <div aria-hidden="true" style={{ position: "absolute", left: "-10000px", top: "auto", width: 1, height: 1, overflow: "hidden" }}>
                            <label>
                                Website
                                <input
                                    tabIndex={-1}
                                    autoComplete="off"
                                    data-testid="contact-honeypot"
                                    name="website"
                                    type="text"
                                    value={form.website}
                                    onChange={change("website")}
                                />
                            </label>
                        </div>
                        <div className="mt-12 flex items-center gap-6">
                            <button
                                type="submit"
                                data-testid="contact-submit"
                                disabled={submitting}
                                className="inline-flex items-center px-9 py-4 bg-[#1A1A1A] text-[#F9F8F6] text-[11px] uppercase tracking-[0.24em] hover:bg-[#3E3A37] disabled:opacity-60 transition-colors"
                            >
                                {submitting ? "Sending…" : "Send inquiry"}
                            </button>
                            {submitted && (
                                <p data-testid="contact-success" className="text-sm text-[#5C5855]">
                                    Received. We will be in touch shortly.
                                </p>
                            )}
                        </div>
                    </form>
                </div>
            </section>
        </main>
    );
}

const Field = ({ label, required, children }) => (
    <label className="block">
        <span className="asc-overline">
            {label} {required && <span className="text-[#8C7A6B]">*</span>}
        </span>
        <div className="mt-2">{children}</div>
    </label>
);
